import React, { useMemo } from "react";

import AccountRestoreModal from "@/components/AccountRestoreModal";
import { useAuth } from "@/hooks/auth";

/**
 * Site-wide guard that re-opens the AccountRestoreModal whenever the
 * currently-loaded user has `deletedAt` set — covers the hard-reload
 * escape hole in the LoginDialog flow.
 *
 * Original flow: LoginDialog catches the ACCOUNT_SCHEDULED_FOR_DELETION
 * error from /auth/login + opens the modal with the limited-session
 * tokens from the error details. If the user hard-reloads the page
 * mid-modal, the tokens are already in localStorage so /users/my returns
 * the deletedAt user — but no modal renders because that one only opens
 * in response to the login error, not on app boot. This guard fixes
 * that gap by watching `myData.deletedAt` and forcing the modal open
 * whenever it's set.
 *
 * The X close button on the modal already runs logout + clears tokens
 * (see AccountRestoreModal.handleClose), so the only way past the guard
 * is to either recover or log out. Hard-reloading repeatedly just
 * re-shows the modal.
 *
 * `sessionTokens` is null on this path — the user is already authenticated
 * with valid tokens from the previous setSession() call. AccountRestoreModal
 * only calls setSession() when sessionTokens is truthy.
 *
 * `hadSubscription` + `subEndsOn` are unknown on hard-reload (those values
 * come from the login error.details payload, which we no longer have).
 * Falls back to `hadSubscription=false`, which renders the simpler
 * non-sub recovery copy. Acceptable trade-off — the user can still
 * recover; they just don't see the cycle-end nuance text.
 */
const GracePeriodGuard: React.FC = () => {
  const { myData } = useAuth();
  const deletedAt = (myData as any)?.deletedAt ?? null;
  const permanentDeleteScheduledAt = (myData as any)?.permanentDeleteScheduledAt ?? null;
  const email = (myData as any)?.email ?? null;

  const permanentDeleteOn = useMemo(() => {
    if (!permanentDeleteScheduledAt) return null;
    try {
      return new Date(permanentDeleteScheduledAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return null;
    }
  }, [permanentDeleteScheduledAt]);

  const open = !!deletedAt;

  return (
    <AccountRestoreModal
      open={open}
      permanentDeleteOn={permanentDeleteOn}
      userEmail={email}
      hadSubscription={false}
      subEndsOn={null}
      sessionTokens={null}
      onClose={() => {
        // AccountRestoreModal.handleClose already runs logout +
        // clears the localStorage tokens. After that resolves, push
        // a hard-nav home so the app reinitializes with no session.
        window.location.assign("/");
      }}
    />
  );
};

export default GracePeriodGuard;
